import { ReactElement } from "react";
import { informationCircleOutline, volumeHighOutline } from "ionicons/icons";
import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { QuestionType, QuestionTypeEnum } from "../../../../types/Question";
import Card, { cardSurface } from "../../../atoms/Card";
import { INK, inkWash, kitCard } from "../../../atoms/Kit";
import styled from "styled-components";
import Icon from "../../../atoms/Icon";
import { useConfig } from "../../../../contexts/ConfigContext";
import type { QuestionLayout } from "../Question";

interface QuestionDisplayProps {
  answerComp: ReactElement;
  question: QuestionType;
  ruleMessage?: string;
  onReadQuestionClick: () => void;
  cardMargin?: string;
  layout?: QuestionLayout;
  /** Checkbox questions: how many choices are ticked (page layout counter). */
  selectionCount?: number;
}

/** Choice questions show each answer as its own card in the page layout. */
const CHOICE_TYPES: QuestionTypeEnum[] = [QuestionTypeEnum.CHECKBOX, QuestionTypeEnum.RADIOBUTTON];

const QuestionDisplay = ({
  answerComp,
  question,
  ruleMessage,
  onReadQuestionClick,
  cardMargin,
  layout = "card",
  selectionCount,
}: QuestionDisplayProps): ReactElement => {
  const { title, description, is_mandatory_question: mandatory } = question;
  const { t } = useTranslation();

  const { selectedRole } = useConfig();
  const isVocalisationEnabled =
    selectedRole?.attributes?.is_vocalisation_enabled;

  if (layout === "page") {
    const isChoice = CHOICE_TYPES.includes(question.type);
    return (
      <PageQuestion $margin={cardMargin}>
        <QuestionHeader>
          <TitleGroup>
            <PageTitle>{mandatory ? `${title} *` : title}</PageTitle>
          </TitleGroup>
          {isVocalisationEnabled && (
            <HeaderIcon onClick={onReadQuestionClick} icon={volumeHighOutline} />
          )}
        </QuestionHeader>

        {/* The question's help text, in a soft box under the title. */}
        {description && (
          <Hint>
            <IonIcon icon={informationCircleOutline} aria-hidden="true" />
            <span>{description}</span>
          </Hint>
        )}

        {(ruleMessage || Boolean(selectionCount)) && (
          <RuleRow>
            <RuleText>{ruleMessage}</RuleText>
            {Boolean(selectionCount) && (
              <SelectionCount aria-live="polite">
                {t("questionnaire_selected_count", { count: selectionCount })}
              </SelectionCount>
            )}
          </RuleRow>
        )}

        {isChoice ? (
          <ChoiceList>{answerComp}</ChoiceList>
        ) : (
          <AnswerPanel className="question-answer-panel">{answerComp}</AnswerPanel>
        )}
      </PageQuestion>
    );
  }

  return (
    <QuestionCard $margin={cardMargin}>
      {/* Question title + description */}
      <QuestionHeader>
        <TitleGroup>
          <Title>{mandatory ? `${title} *` : title}</Title>
          <SubTitle>{description}</SubTitle>
        </TitleGroup>
        {isVocalisationEnabled && (
          <HeaderIcon onClick={onReadQuestionClick} icon={volumeHighOutline} />
        )}
      </QuestionHeader>
      <Desc>{ruleMessage}</Desc>

      {answerComp}
    </QuestionCard>
  );
};

const QuestionCard = styled(Card)`
  padding: 22px;
  ${cardSurface}
  // overflow: auto;
  // max-height: 600px;

  // @media (max-height: 730px) {
  //   max-height: 400px;
  // }
  // @media (max-height: 630px) {
  //   max-height: 350px;
  // }
  // @media (max-height: 580px) {
  //   max-height: 300px;
  // }
  // @media (max-height: 530px) {
  //   max-height: 250px;
  // }
  // @media (max-height: 480px) {
  //   max-height: 200px;
  // }
`;

const QuestionHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const TitleGroup = styled.div`
  /* Allow the title block to shrink/wrap so it doesn't squeeze the icon. */
  min-width: 0;
`;

const HeaderIcon = styled(Icon)`
  color: ${(props) => props.theme.primary.main};
  padding: 0px;
  font-size: 20px;
  /* Keep the speaker icon at full size even when the title is long. */
  flex-shrink: 0;
`;

const Title = styled.div`
  color: ${(props) => props.theme.primary.main};
  font-size: 18px;
  font-weight: 600;
  /* Question titles follow the theme's title font (a div, so the global
     h1-h6 heading rule in App.scss doesn't reach it). */
  font-family: var(--app-font-heading, var(--app-font-family, inherit));
`;

const SubTitle = styled.div`
  font-size: 14px;
  color: #6b6b6b;
  margin-top: 2px;
`;

const Desc = styled.div`
  color: grey;
  font-size: 14px;
  margin-top: 6px;
  border-bottom: 1px solid #ececec;
  padding-bottom: 8px;
`;

/* --- Page layout (questionnaire screen) --- */

const PageQuestion = styled.section<{ $margin?: string }>`
  margin: ${({ $margin }) => $margin ?? "14px 20px 20px"};
`;

const PageTitle = styled.h2`
  margin: 0;
  font-family: var(--app-font-heading, var(--app-font-family, inherit));
  font-size: 22px;
  line-height: 1.3;
  font-weight: var(--app-font-heading-weight, 700);
  letter-spacing: -0.015em;
  color: var(--app-color-text, #0f172a);
`;

const Hint = styled.p`
  margin: 10px 0 0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-radius: var(--app-radius-answer, 18px);
  background: ${inkWash(0.06)};
  box-shadow: inset 0 0 0 1px ${inkWash(0.1)};
  font-size: 14.5px;
  line-height: 1.45;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.78);

  ion-icon {
    flex: none;
    margin-top: 1px;
    font-size: 18px;
    color: ${INK};
  }
`;

const RuleRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const RuleText = styled.span`
  font-size: 13.5px;
  line-height: 1.35;
  font-weight: 700;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.7);
`;

const SelectionCount = styled.span`
  flex: none;
  font-size: 13.5px;
  font-weight: 700;
  color: ${INK};
  white-space: nowrap;
`;

const ChoiceList = styled.div`
  margin-top: 10px;
`;

/* Non-choice inputs (number, date, text, slider, dropdown, hour) sit inside one
   answer card. The card is the field (theme/radio.scss drops the Ionic row
   underlines), so typing in it shows an accent ring instead. */
const AnswerPanel = styled.div`
  margin-top: 10px;
  padding: 6px 12px;
  ${kitCard}
  transition: outline-color 0.15s ease;
  outline: 2px solid transparent;
  outline-offset: -1px;

  &:has(input:focus, textarea:focus) {
    outline-color: rgba(var(--app-color-accent-rgb, 66, 133, 244), 0.55);
  }
`;

export default QuestionDisplay;
